import React, { useEffect, useState, useCallback, useContext, useMemo } from "react";
import { Box, TextField, InputAdornment, List, ListItem, ListItemText, Collapse, IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { getCookies } from "../../services/Cookies";
import { ThemeContext } from "../../context/ThemeContext";
import { useTranslation } from 'react-i18next';
import { useSymbolPrices } from '../../context/WebSocketContext';
import { useSelector } from "react-redux";


const SearchDropdown = ({ onSearchChange, setIsSearchDropdownOpen }) => {
  const [groupedData, setGroupedData] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [expanded, setExpanded] = useState({});
  const [isFocused, setIsFocused] = useState(false);
  const clientId = getCookies("clientId");
  const { darkMode } = useContext(ThemeContext);
  const { t } = useTranslation();
  // const symbols = useSymbolPrices();
  const { symbols } = useSelector(state => state.watchlist)
  const { group = [] } = useSelector((state) => state.groupUnit)

  // Get hidden symbols from local storage
  const [hiddenSymbols, setHiddenSymbols] = useState(
    JSON.parse(localStorage.getItem(`hiddenSymbols_${clientId}`)) || []
  );

  const groupNames = useMemo(() => {
    return group.reduce((acc, g) => {
      acc[g.id] = g.name;
      return acc;
    }, {});
  }, [group]);

  useEffect(() => {
    if (symbols && Object.keys(symbols).length > 0) {
      const grouped = Object.values(symbols).reduce((acc, symbol) => {
        if (!acc[symbol.parentId]) {
          acc[symbol.parentId] = [];
        }
        acc[symbol.parentId].push(symbol);
        return acc;
      }, {});

      setGroupedData(grouped);
    }
  }, [symbols]);



  // Handle search input
  const handleSearch = useCallback((event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
    onSearchChange(query);
  }, [onSearchChange]);

  const handleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleClose = () => {
    setSearchQuery("");
    onSearchChange("");
    setIsFocused(false);
    setIsSearchDropdownOpen(false);
  };

  const handleFocus = () => {
    setIsFocused(true);
    setIsSearchDropdownOpen(true);
  };

  const handleToggleSymbolVisibility = (symbolId) => {
    const updatedHiddenSymbols = hiddenSymbols.includes(symbolId)
      ? hiddenSymbols.filter((id) => id !== symbolId)
      : [...hiddenSymbols, symbolId];

    setHiddenSymbols(updatedHiddenSymbols);
    localStorage.setItem(`hiddenSymbols_${clientId}`, JSON.stringify(updatedHiddenSymbols));
  };

  return (
    <>
      <Box display="flex" alignItems="center" p={0}>
        <TextField placeholder={t('searchSymbol')} variant="standard" fullWidth value={searchQuery} onChange={handleSearch} onFocus={handleFocus} InputProps={{
          disableUnderline: true, startAdornment: (
            <InputAdornment position="start"><SearchIcon /></InputAdornment>
          ),
        }}
        />
        {isFocused && (
          <IconButton onClick={handleClose} size="small">
            <CloseIcon sx={{ fontSize: '15px' }} />
          </IconButton>
        )}
      </Box>

      {isFocused && (
        <List sx={{ maxHeight: 400, overflowY: "auto", p: 0, color: darkMode ? '#f5f5f5' : 'var(--black)' }}>
          {Object.keys(groupedData).map((parentId) => {
            const filteredSymbols = groupedData[parentId].filter(symbol =>
              symbol.name && symbol.name.toLowerCase().includes(searchQuery)


            );
            if (filteredSymbols.length === 0) return null;

            return (
              <React.Fragment key={parentId}>
                <ListItem button onClick={() => handleExpand(parentId)} sx={{ p: 1, fontSize: "12px" }}>
                  <ListItemText primary={groupNames[parentId] || `Group ${parentId}`} primaryTypographyProps={{ fontSize: "13.5px", color: darkMode ? "#f5f5f5" : "var(--black)", }} />
                  {expanded[parentId] ? (
                    <ExpandLessIcon sx={{ fontSize: "14px" }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: "14px" }} />
                  )}
                </ListItem>

                <Collapse in={expanded[parentId]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ color: darkMode ? '#f5f5f5' : 'var(--black)' }}>
                    {filteredSymbols.map((symbol) => (
                      <ListItem key={symbol.id} button sx={{ display: "flex", justifyContent: "space-between" }}>
                        <ListItemText primary={symbol.name} primaryTypographyProps={{ fontSize: "13.5px", color: darkMode ? "#f5f5f5" : "var(--black)", }} />
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggleSymbolVisibility(symbol.id); }}>
                          {hiddenSymbols.includes(symbol.id) ? (
                            <AddIcon sx={{ fontSize: "14px" }} />
                          ) : (
                            <CheckIcon sx={{ fontSize: "14px", color: 'green' }} />
                          )}
                        </IconButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          })}
        </List>
      )}
    </>
  );
};

export default SearchDropdown;
